import { useCurrentUserRoles } from '@data-delivery/permission-ui-data-access-state';
import { type ReactNode, Suspense } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { type Permissions, hasPermission } from '../utils/abac';
import { PATHS } from '../utils/routing';
import { FallbackLoader } from './top-progress-bar';

type BGResourceType = 'users' | 'assignments' | 'products';
type PermissionAction = 'view' | 'create' | 'update' | 'delete';

interface BusinessGroupProtectedRouteProps {
  children: ReactNode;
  resource: BGResourceType;
  action: PermissionAction;
  fallbackPath?: string;
}

/**
 * Protected route that checks permissions scoped to a business group.
 * Requires businessGroupId in URL params.
 */
export const BusinessGroupProtectedRoute: React.FC<BusinessGroupProtectedRouteProps> = ({
  children,
  resource,
  action,
  fallbackPath = PATHS.notAuthorized,
}) => {
  const { businessGroupId } = useParams<{ businessGroupId: string }>();
  const { data: userRoles, isLoading, isError } = useCurrentUserRoles();

  if (isLoading) {
    return <FallbackLoader />;
  }

  if (isError || !userRoles || userRoles.length === 0) {
    return <Navigate to={fallbackPath} replace />;
  }

  if (!businessGroupId) {
    return <Navigate to={fallbackPath} replace />;
  }

  // For permission checking, we only need the businessGroup field
  // The permission functions check businessGroup access, not full entity validity
  let hasAccess = false;

  // For all resources (users, assignments, products), create minimal object with businessGroup
  const mockData = { businessGroup: businessGroupId };
  hasAccess = hasPermission(
    userRoles,
    resource,
    action,
    mockData as Permissions[typeof resource]['dataType'],
  );

  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <Suspense fallback={<FallbackLoader />}>{children}</Suspense>;
};
